import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PulseReport from "../components/PulseReport";
import { SocialShareModal } from "../components/SocialShare";
import { buildPulseCard } from "../lib/shareCard";
import { toast } from "sonner";
import { Share2, Copy, Check, Megaphone } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export default function Pulse() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [refCode, setRefCode] = useState(null);

  useEffect(() => {
    api.get("/pulse").then((r) => setPulse(r.data)).catch(() => toast.error(t("pulse.couldnTLoadPulse")));
    api.get("/referrals").then((r) => setRefCode(r.data.ref_code)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createShare = async () => {
    setCreating(true);
    try {
      const r = await api.post("/pulse/share");
      const url = `${window.location.origin}/pulse/${r.data.token}`;
      setShareUrl(url);
      toast.success(t("pulse.parentShareLinkCreatedValid"));
    } catch { toast.error(t("pulse.couldnTCreateShareLink")); }
    finally { setCreating(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const buildCanvas = useCallback(() => buildPulseCard({
    name: (user?.name || "My").split(" ")[0],
    stats: {
      ayahs: pulse?.ayahs_memorized_week ?? 0,
      activeDays: pulse?.active_days ?? 0,
      reviews: pulse?.coach?.reviews_completed_week ?? 0,
      streak: pulse?.streak ?? 0,
    },
    url: window.location.origin.replace(/^https?:\/\//, ""),
  }), [pulse, user]);

  if (!pulse) {
    return <div className="text-muted-foreground" data-testid="pulse-loading">{t("pulse.loadingYourWeek")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="pulse-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-sm text-muted-foreground">{t("pulse.aWeeklySnapshotShareWith")}</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setSocialOpen(true)} className="btn-primary text-sm" data-testid="social-share-btn">
            <Megaphone className="h-4 w-4" /> {t("pulse.shareMyWeek")}
          </button>
          {!shareUrl ? (
            <button onClick={createShare} disabled={creating} className="btn-accent text-sm" data-testid="create-share-btn">
              <Share2 className="h-4 w-4" /> {creating ? t("pulse.creating") : t("pulse.shareWithParent")}
            </button>
          ) : (
            <div className="flex items-center gap-2 bg-white rounded-full border border-sand-300 px-3 py-1.5 text-xs" data-testid="share-url-bar">
              <span className="text-muted-foreground truncate max-w-[260px]">{shareUrl}</span>
              <button onClick={copy} className="text-forest-700 font-semibold inline-flex items-center gap-1" data-testid="copy-share-btn">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t("achievements.copied") : t("achievements.copy")}
              </button>
            </div>
          )}
        </div>
      </div>
      <PulseReport pulse={pulse} />

      <SocialShareModal
        open={socialOpen}
        onClose={() => setSocialOpen(false)}
        buildCanvas={buildCanvas}
        message={t("pulse.shareMessage", { ayahs: pulse.ayahs_memorized_week ?? 0, days: pulse.active_days ?? 0, streak: pulse.streak ?? 0 })}
        url={shareUrl ? `${shareUrl}?ref=${refCode || ""}` : `${window.location.origin}/?ref=${refCode || ""}`}
        filename="hikmahverses-pulse.png"
      />
    </div>
  );
}
